import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Panel } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Trash2, Tags } from "lucide-react";
import { FLAG_LABELS, loadTripCodes, setTripCodes } from "@/lib/tripCodes";
import { useConfirm } from "@/lib/confirm";

const field = "w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-600";
const blank = () => ({ code: "", meaning: "", aba: false, wheelchair: false, alone: false, shared: false, monitor: false, escort: false });

export function TripCodesPanel({ canEdit }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const ask = useConfirm();

  useEffect(() => { loadTripCodes(true).then((d) => setRows(d.codes || [])); }, []);
  useEffect(() => {
    if (rows && window.location.hash === "#trip-codes") document.getElementById("trip-codes")?.scrollIntoView({ behavior: "smooth" });
  }, [rows]);
  const set = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const save = async () => {
    if (!(await ask({ title: "Save trip codes?", description: "Dispatch, AI and Group runs use these rules right away.", confirmLabel: "Save codes" }))) return;
    setSaving(true);
    try {
      const { data } = await api.put("/trip-codes", { codes: rows.filter((r) => r.code.trim()) });
      setRows(data.codes); setTripCodes(data.codes); loadTripCodes(true);
      toast.success("Trip codes saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div id="trip-codes">
    <Panel className="mt-6" title="Trip Codes" testid="trip-codes-panel">
      <div className="space-y-3 p-5">
        <p className="flex items-start gap-2.5 text-sm text-slate-400"><Tags className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
          Codes found in an imported trip's type, mobility notes or instructions tell dispatch how to handle it. "Ride alone" riders never join a group run; "Wheelchair van" needs an accessible vehicle; escort and monitor take an extra seat. NEMT trips without codes work exactly as before.</p>
        {!rows ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-xs">
              <thead><tr className="text-left uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-2">Code</th><th className="py-2 pr-2">Meaning</th>
                {Object.values(FLAG_LABELS).map((l) => <th key={l} className="px-1 py-2 text-center">{l}</th>)}<th />
              </tr></thead>
              <tbody>{rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-800" data-testid={`trip-code-row-${i}`}>
                  <td className="w-32 py-1.5 pr-2"><input value={r.code} disabled={!canEdit} onChange={(e) => set(i, { code: e.target.value.toUpperCase() })} className={`${field} font-mono`} data-testid={`trip-code-code-${i}`} /></td>
                  <td className="py-1.5 pr-2"><input value={r.meaning} disabled={!canEdit} onChange={(e) => set(i, { meaning: e.target.value })} className={field} data-testid={`trip-code-meaning-${i}`} /></td>
                  {Object.keys(FLAG_LABELS).map((k) => (
                    <td key={k} className="px-1 py-1.5 text-center"><input type="checkbox" checked={!!r[k]} disabled={!canEdit} onChange={(e) => set(i, { [k]: e.target.checked })} className="h-4 w-4 accent-blue-600" data-testid={`trip-code-${k}-${i}`} /></td>
                  ))}
                  <td className="py-1.5 pl-2">{canEdit && <button onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove code" className="text-slate-500 hover:text-red-300" data-testid={`trip-code-delete-${i}`}><Trash2 className="h-4 w-4" /></button>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {canEdit && rows && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setRows([...rows, blank()])} className="gap-1.5 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white" data-testid="trip-code-add"><Plus className="h-4 w-4" /> Add code</Button>
            <Button onClick={save} disabled={saving} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="trip-codes-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save trip codes</Button>
          </div>
        )}
      </div>
    </Panel>
    </div>
  );
}
